import React, { useEffect, useMemo, useState } from "react";
import { Box, Collapse, Typography, makeStyles } from "@material-ui/core";
import { AsyncSection, Badge, Drawer, Icon, SegmentedControl } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import { fetchAnalytics } from "./adTemplateApi";
import { freeMeter, int, money, MODE_LABEL, relativeDay } from "./formatters";
import AdDetailList from "./AdDetailList";
import AdCardDialog from "./AdCardDialog";
import VariantList from "./VariantList";
import FormatBadge from "./FormatBadge";

const useStyles = makeStyles({
  summary: {
    display: "flex",
    flexWrap: "wrap",
    gap: 18,
    paddingBottom: 14,
    borderBottom: `1px solid ${tokens.divider}`,
  },
  stat: { minWidth: 92 },
  statValue: {
    fontFamily: tokens.fontHeading,
    fontWeight: 700,
    fontSize: 18,
    color: "#232D2F",
    fontVariantNumeric: "tabular-nums",
  },
  statLabel: {
    fontFamily: tokens.fontBody,
    fontSize: 11,
    fontWeight: 600,
    color: tokens.textSecondary,
    marginTop: 1,
  },
  meta: {
    fontFamily: tokens.fontBody,
    fontSize: 12,
    color: tokens.textHint,
    marginTop: 10,
  },
  tabs: { margin: "16px 0 6px" },
  run: { padding: "12px 0", borderBottom: `1px solid ${tokens.divider}` },
  runTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  date: { fontFamily: tokens.fontBody, fontSize: 11.5, color: tokens.textHint },
  prompt: {
    fontFamily: tokens.fontBody,
    fontSize: 13,
    color: tokens.textPrimary,
    marginTop: 4,
    wordBreak: "break-word",
  },
  runMeta: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontFamily: tokens.fontBody,
    fontSize: 12,
    color: tokens.textSecondary,
    marginTop: 6,
  },
  expander: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    border: "none",
    background: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: tokens.fontBody,
    fontSize: 12,
    fontWeight: 700,
    color: tokens.link,
  },
  expanderOff: { color: tokens.textHint, cursor: "default", fontWeight: 400 },
  nested: {
    marginTop: 6,
    paddingLeft: 12,
    borderLeft: `2px solid ${tokens.primaryLight}`,
  },
});

const Stat = ({ classes, value, label }) => (
  <Box className={classes.stat}>
    <Typography component="div" className={classes.statValue}>{value}</Typography>
    <Typography component="div" className={classes.statLabel}>{label}</Typography>
  </Box>
);

export default function ProjectDrawer({ project, range, onClose, initialTab = "runs" }) {
  const classes = useStyles();
  const [tab, setTab] = useState(initialTab);
  const [expanded, setExpanded] = useState({});
  const [runs, setRuns] = useState({ loading: true, notFound: false, items: [], error: null });
  const [ads, setAds] = useState({ loading: true, notFound: false, items: [], error: null });
  const [variants, setVariants] = useState({ loading: false, items: [], error: null });
  const [openAd, setOpenAd] = useState(null);

  useEffect(() => {
    setTab(initialTab);
    setExpanded({});
  }, [project, initialTab]);

  useEffect(() => {
    if (!project) return undefined;
    let alive = true;
    setRuns({ loading: true, notFound: false, items: [], error: null });
    fetchAnalytics(`project/${project.assistantId}/runs`, {
      from: range.from,
      to: range.to,
      page: 1,
    }).then((r) => {
      if (!alive) return;
      setRuns({
        loading: false,
        notFound: r.notFound,
        items: (r.data && r.data.items) || [],
        error: r.error,
      });
    });
    return () => {
      alive = false;
    };
  }, [project, range.from, range.to]);

  useEffect(() => {
    if (!project) return undefined;
    let alive = true;
    setAds({ loading: true, notFound: false, items: [], error: null });
    fetchAnalytics(`project/${project.assistantId}/ads`, { page: 1 }).then((r) => {
      if (!alive) return;
      setAds({
        loading: false,
        notFound: r.notFound,
        items: (r.data && r.data.items) || [],
        error: r.error,
      });
    });
    return () => {
      alive = false;
    };
  }, [project]);

  useEffect(() => {
    const templateIds = runs.items.map((run) => run.templateId).filter(Boolean);
    if (!project || !templateIds.length) {
      setVariants({ loading: false, items: [], error: null });
      return undefined;
    }
    let alive = true;
    setVariants({ loading: true, items: [], error: null });
    fetchAnalytics(`project/${project.assistantId}/variants`, {
      templateIds: templateIds.join(","),
    }).then((r) => {
      if (!alive) return;
      setVariants({
        loading: false,
        items: (r.data && r.data.items) || [],
        error: r.error,
      });
    });
    return () => {
      alive = false;
    };
  }, [project, runs.items]);

  const variantsByTemplate = useMemo(() => {
    const grouped = {};
    variants.items.forEach((variant) => {
      const key = String(variant.templateId);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(variant);
    });
    return grouped;
  }, [variants.items]);

  const adsByTemplate = useMemo(() => {
    const grouped = {};
    ads.items.forEach((ad) => {
      const templateId = ad.adTemplate && ad.adTemplate.templateId;
      if (!templateId) return;
      const key = String(templateId);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(ad);
    });
    return grouped;
  }, [ads.items]);

  if (!project) return null;

  const toggle = (templateId) =>
    setExpanded((prev) => ({ ...prev, [templateId]: !prev[templateId] }));

  return (
    <Drawer
      open
      onClose={onClose}
      width={660}
      title={project.projectName || project.assistantId}
    >
      <div data-testid="project-drawer">
        <Box className={classes.summary}>
          <Stat classes={classes} value={int(project.runs)} label="Prompts" />
          <Stat classes={classes} value={int(project.adsGenerated)} label="Ads generated" />
          <Stat classes={classes} value={money(project.spent, project.currency)} label="Billed" />
          <Stat classes={classes} value={int(project.liveAds)} label="Live ads" />
          <Stat
            classes={classes}
            value={freeMeter(project.freeLeft, project.freeLimit)}
            label="Free left"
          />
        </Box>

        <Typography component="div" className={classes.meta}>
          Project {project.assistantId} · Client {project.clientId || "—"}
          {project.partnerId ? ` · Partner ${project.partnerId}` : ""} · {project.currency}
        </Typography>

        <Box className={classes.tabs}>
          <SegmentedControl
            value={tab}
            onChange={setTab}
            data-testid="drawer-tabs"
            options={[
              { value: "runs", label: `Prompts (${int(runs.items.length)})` },
              { value: "ads", label: `Live ads (${int(ads.items.length)})` },
            ]}
          />
        </Box>

        {tab === "runs" && (
          <AsyncSection
            loading={runs.loading}
            notFound={runs.notFound}
            error={runs.error}
            empty={!runs.items.length}
            emptyTitle="No prompts in this range"
            emptyDescription="Widen the date range to see older prompts."
          >
            <Box data-testid="drawer-runs">
              {runs.items.map((run) => {
                const runAds = adsByTemplate[String(run.templateId)] || [];
                const runVariants = variantsByTemplate[String(run.templateId)] || [];
                const isOpen = Boolean(expanded[run.templateId]);
                return (
                  <Box
                    key={run.templateId}
                    className={classes.run}
                    data-testid={`run-${run.templateId}`}
                  >
                    <Box className={classes.runTop}>
                      <Typography component="span" className={classes.date}>
                        {relativeDay(run.createdAt)}
                      </Typography>
                      <Badge tone={run.mode === "charged" ? "resolved" : "warning"} subtle>
                        {run.mode === "charged"
                          ? money(run.amount, run.currency)
                          : MODE_LABEL[run.mode] || run.mode}
                      </Badge>
                    </Box>

                    <Typography component="div" className={classes.prompt}>
                      {run.prompt || "—"}
                    </Typography>

                    <Box className={classes.runMeta}>
                      <FormatBadge item={run} />
                      <span>
                        {int(run.delivered)} of {int(run.requested)} ads
                      </span>
                      <span>{int(runAds.length)} live</span>
                      {runVariants.length ? (
                        <button
                          type="button"
                          className={classes.expander}
                          data-testid={`run-ads-toggle-${run.templateId}`}
                          onClick={() => toggle(run.templateId)}
                        >
                          <Icon name={isOpen ? "chevron-up" : "chevron-down"} size={14} />
                          See {int(runVariants.length)} generated
                        </button>
                      ) : (
                        <span
                          className={`${classes.expander} ${classes.expanderOff}`}
                          data-testid={`run-ads-none-${run.templateId}`}
                        >
                          {variants.loading ? "loading…" : "nothing generated"}
                        </span>
                      )}
                    </Box>

                    <Collapse in={isOpen} unmountOnExit>
                      <Box className={classes.nested} data-testid={`run-ads-${run.templateId}`}>
                        <VariantList variants={runVariants} onSelect={setOpenAd} />
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </AsyncSection>
        )}

        {tab === "ads" && (
          <AsyncSection
            loading={ads.loading}
            notFound={ads.notFound}
            error={ads.error}
            empty={!ads.items.length}
            emptyTitle="No live ads from this project"
            emptyDescription="Ads launched from a generated template show up here with their Meta performance."
          >
            <Box data-testid="drawer-ads">
              <AdDetailList ads={ads.items} onSelect={setOpenAd} />
            </Box>
          </AsyncSection>
        )}

        <AdCardDialog ad={openAd} onClose={() => setOpenAd(null)} />
      </div>
    </Drawer>
  );
}
